//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { useEffect, useState } from 'react'

import { Box, Grid, Tooltip, Typography, useTheme } from '@mui/material'
import {
  LiquidityAsset,
  PoolAccountCTO,
  PoolBaseAccountCTO,
  PoolBaseOverviewCTO,
  PoolDynamicAccountCTO,
  PoolFlexAccountCTO,
} from '@shared/sharedTypes'
import { useOtChainState, useOtSelector } from '@react/redux/store'

import Paper from '@mui/material/Paper'

import { erc20WeiToCommaDecString } from '@shared/conversions'

import { PoolTypeEnum } from '@shared/Enums'

import { EthAddr, ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { logOT } from '@shared/loggingShared'
import PortfolioValueSkeleton from '@react/components/elements/PortfolioValueSkeleton'
import ViewLogo from '@react/components/elements/ViewLogo'

export default function LenderPortfolioSummary(): React.ReactElement {
  const poolOverviewCTOs = useOtSelector((state) => state.poolState.poolOverviewCTOs)

  const {
    chainConfiguration: { perimeterAddresses },
    userConnection: { accountAddr },
  } = useOtChainState()

  const poolAccountCTOs = useOtSelector((state) => state.poolState.poolAccountCTOs)
  const liquidityAssets = perimeterAddresses?.liquidityAssets ?? []
  const theme = useTheme()
  const poolAddrs: EthAddr[] = Object.keys(poolOverviewCTOs)

  const [portfolioValues, setPortfolioValues] = useState<Record<string, bigint>>({})

  // useEffect(() => {
  //   poolAddrs.forEach((poolAddr: EthAddr) => {
  //     const poolBaseOverviewCTO = poolOverviewCTOs[poolAddr] as PoolBaseOverviewCTO
  //     const poolBaseAccountCTO =
  //       accountAddr && poolAddr ? (poolAccountCTOs?.[poolAddr]?.[accountAddr] as PoolBaseAccountCTO) : undefined

  //     if (poolBaseOverviewCTO.poolType == PoolTypeEnum.Term) {
  //       const poolAccountCTO = poolBaseAccountCTO as PoolAccountCTO
  //       if (!poolAccountCTO && accountAddr) {
  //         dispatch(poolActions.buildPoolAccountCTO({ poolAddr, accountAddr }))
  //       }
  //     } else {
  //       const poolAccountFlex = poolBaseAccountCTO as PoolFlexAccountCTO

  //       if (!poolAccountFlex && accountAddr) {
  //         dispatch(poolActions.buildPoolAccountCTO({ poolAddr, accountAddr }))
  //       }
  //     }
  //   })
  // }, [poolOverviewCTOs])

  useEffect(() => {
    if (liquidityAssets && poolAddrs) {
      const newPV: Record<string, bigint> = {}
      liquidityAssets.forEach((liquidityAsset: LiquidityAsset) => {
        const assetAddr = liquidityAsset.addr

        newPV[assetAddr] = 0n
      })

      poolAddrs.forEach((poolAddr: EthAddr) => {
        const poolBaseOverviewCTO = poolOverviewCTOs[poolAddr] as PoolBaseOverviewCTO

        const liquidityAssetAddr = poolBaseOverviewCTO.liquidityAssetAddr
        const poolBaseAccountCTO =
          accountAddr && poolAddr ? (poolAccountCTOs?.[poolAddr]?.[accountAddr] as PoolBaseAccountCTO) : undefined

        if (poolBaseAccountCTO) {
          switch (poolBaseOverviewCTO.poolType) {
            case PoolTypeEnum.Term: {
              const poolAccountCTO = poolBaseAccountCTO as PoolAccountCTO
              const dollars = BigInt(poolAccountCTO?.totalAssetCurrent ?? '0')
              const sum = BigInt(newPV[liquidityAssetAddr] ?? '0') + dollars
              newPV[liquidityAssetAddr] = sum
              break
            }
            case PoolTypeEnum.FlexibleRate: {
              const poolAccountFlex = poolBaseAccountCTO as PoolFlexAccountCTO
              const dollars = BigInt(poolAccountFlex?.accountOutstandingLoanPrincipals ?? '0')
              const sum = BigInt(newPV[liquidityAssetAddr] ?? '0') + dollars
              newPV[liquidityAssetAddr] = sum
              break
            }
            case PoolTypeEnum.Dynamic: {
              const poolAccountDynamic = poolBaseAccountCTO as PoolDynamicAccountCTO
              const dollars = BigInt(poolAccountDynamic?.principalEarningInterest ?? '0')
              const sum = BigInt(newPV[liquidityAssetAddr] ?? '0') + dollars
              newPV[liquidityAssetAddr] = sum
              break
            }
            default:
              logOT.warn('Unsupported pool type', poolBaseOverviewCTO.poolType)
              break
          }
        }
      })

      setPortfolioValues(newPV)
    }
  }, [perimeterAddresses, poolAccountCTOs, poolOverviewCTOs])

  const isLoading =
    Object.keys(portfolioValues).length === 0 ||
    !perimeterAddresses ||
    !liquidityAssets ||
    poolAddrs.length === 0 ||
    !accountAddr

  return (
    <Paper
      key="paper1"
      data-testid="paper1"
      sx={{
        marginTop: 4,
        marginBottom: 4,
        padding: '24px',
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ marginBottom: 3 }}>
        <Tooltip title="The total value of all your investments if held to maturity.">
          <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: 3 }}>
            Portfolio Balance
          </Typography>
        </Tooltip>
      </Box>

      {isLoading ? (
        <PortfolioValueSkeleton />
      ) : (
        <Grid container spacing={3}>
          {Object.keys(portfolioValues).map((key, index) => {
            const addr = key
            const val = portfolioValues[key]
            const asset = liquidityAssets.find((asset: LiquidityAsset) => asset.addr === addr)

            // Skip ETH_ADDR_ZERO or assets with no value
            if (addr === ETH_ADDR_ZERO || !asset || val === 0n) {
              return null
            }

            return (
              <Grid item xs={12} sm={6} md={4} key={`asset-card-${index}`}>
                <Paper
                  elevation={0}
                  sx={{
                    padding: 3,
                    backgroundColor: theme.palette.grey[100],
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    minHeight: 120,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 2,
                      gap: 1,
                    }}
                  >
                    <ViewLogo addrOrId={addr} size={48} />
                    <Typography
                      sx={{
                        color: theme.palette.text.muted,
                      }}
                    >
                      {asset.symbol}
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      lineHeight: 1.2,
                    }}
                  >
                    ${erc20WeiToCommaDecString(val)}
                  </Typography>
                </Paper>
              </Grid>
            )
          })}
        </Grid>
      )}
    </Paper>
  )
}
